import { fetchUserAttributes } from 'aws-amplify/auth';
import { HTTP_CONFIG } from './HttpConfig';
import { HttpError } from './HttpError';

/**
 * Generic HTTP Client
 */
export class HttpClient {
    constructor(config = {}) {
        this.baseUrl = config.baseUrl || HTTP_CONFIG.BASE_URL;
        this.defaultHeaders = { ...HTTP_CONFIG.DEFAULT_HEADERS, ...config.headers };
        this.timeout = config.timeout || HTTP_CONFIG.DEFAULT_TIMEOUT;
        this.requestInterceptor = config.onRequest || null;
        this.responseInterceptor = config.onResponse || null;
        this.errorInterceptor = config.onError || null;
        this.getCurrentUserAttributes = async () => {
            const attrs = await fetchUserAttributes();
            return attrs;
        }
    }

    /**
     * Generic request method
     */
    async request(endpoint, options = {}) {
        const url = this.buildUrl(endpoint);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        // Build config
        const config = {
            method: 'GET',
            ...options,
            headers: { ...this.defaultHeaders, ...options.headers },
            signal: controller.signal,
        };

        // Add AWS Cognito authorization token
        await this.addAuthHeader(config);

        // Stringify body if needed
        if (options.body && typeof options.body !== 'string') {
            config.body = JSON.stringify(options.body);
        }

        // Log request if interceptor is set
        if (this.requestInterceptor) {
            this.requestInterceptor({ url, config });
        }

        try {
            const response = await fetch(url, config);
            clearTimeout(timeoutId);

            // Log response if interceptor is set
            if (this.responseInterceptor) {
                this.responseInterceptor({ url, config, response });
            }

            if (!response.ok) {
                throw await this.createErrorFromResponse(response);
            }

            return await this.parseResponse(response);
        } catch (error) {
            clearTimeout(timeoutId);

            const normalizedError = this.normalizeError(error);

            // Log error if interceptor is set
            if (this.errorInterceptor) {
                this.errorInterceptor({ url, config, error: normalizedError });
            }

            throw normalizedError;
        }
    }

    /**
     * Add AWS Cognito authorization header
     */
    async addAuthHeader(config) {
        try {
            const token = await this.getAuthToken();
            if (token) {
                config.headers = {
                    ...config.headers,
                    'Authorization': `Bearer ${token}`,
                };
            }
        } catch (error) {
            console.warn('Failed to get AWS auth token:', error.message);
            // Some endpoints might not require auth, so continue
        }
    }

    /**
     * Get AWS Cognito access token using Amplify
     */
    async getAuthToken() {
        try {
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            return session.tokens?.accessToken?.toString();
        } catch (error) {
            console.error('Error fetching AWS auth token:', error);
            return null;
        }
    }

    /**
     * HTTP Method shortcuts
     */
    async get(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'GET' });
    }

    async post(endpoint, data, options = {}) {
        return this.request(endpoint, { ...options, method: 'POST', body: data });
    }

    async put(endpoint, data, options = {}) {
        return this.request(endpoint, { ...options, method: 'PUT', body: data });
    }

    async patch(endpoint, data, options = {}) {
        return this.request(endpoint, { ...options, method: 'PATCH', body: data });
    }

    async delete(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'DELETE' });
    }

    /**
     * Helper methods
     */
    buildUrl(endpoint) {
        if (endpoint.startsWith('http')) {
            return endpoint;
        }
        return `${this.baseUrl}${endpoint}`;
    }

    async parseResponse(response) {
        if (response.status === 204) {
            console.debug('204 No Content response');
            return { success: true, status: 204 };
        }
        const contentType = response.headers.get('content-type');

        if (!contentType) {
            return null;
        }

        if (contentType.includes('application/json')) {
            const data = await response.json();
            // Extract data from ApiResponse wrapper if present
            return data.data !== undefined ? data.data : data;
        }

        if (contentType.includes('text/')) {
            return await response.text();
        }

        return await response.blob();
    }

    async createErrorFromResponse(response) {
        try {
            const errorData = await response.json();
            const message = errorData.message || `HTTP ${response.status}`;
            return new HttpError(response.status, message, errorData);
        } catch {
            return HttpError.fromResponse(response);
        }
    }

    normalizeError(error) {
        if (error instanceof HttpError) {
            return error;
        }

        if (error.name === 'AbortError') {
            return HttpError.timeoutError();
        }

        if (error.name === 'TypeError') {
            return HttpError.networkError();
        }

        return new HttpError(500, error.message || 'Unknown error occurred');
    }

    /**
     * Configuration methods
     */
    setBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
        return this;
    }

    setDefaultHeader(key, value) {
        this.defaultHeaders[key] = value;
        return this;
    }

    removeDefaultHeader(key) {
        delete this.defaultHeaders[key];
        return this;
    }
}